// src/utils/similarity.js

/**
 * Calculates the cosine similarity between two vectors.
 * @param {number[]} vecA - The first vector (embedding).
 * @param {number[]} vecB - The second vector (embedding).
 * @returns {number} A value between 0 and 1 (or -1 if possible, but 0-1 for normalized embeddings).
 */
const cosineSimilarity = (vecA, vecB) => {
    if (!vecA || !vecB || vecA.length !== vecB.length) {
        return 0;
    }

    let dotProduct = 0.0;
    let magA = 0.0;
    let magB = 0.0;

    for (let i = 0; i < vecA.length; i++) {
        dotProduct += vecA[i] * vecB[i];
        magA += vecA[i] * vecA[i];
        magB += vecB[i] * vecB[i];
    }

    magA = Math.sqrt(magA);
    magB = Math.sqrt(magB);

    if (magA === 0 || magB === 0) {
        return 0;
    }

    return dotProduct / (magA * magB);
};

/**
 * Finds the top N similar articles based on embedding.
 * @param {string} currentArticlePath - The path of the article being viewed.
 * @param {Array} allArticles - The full array of articles (with 'embedding').
 * @param {number} topN - How many similar articles to return.
 * @returns {Array} An array of the top N similar articles.
 */
export const findSimilarArticles = (currentArticlePath, allArticles, topN = 3) => {
    const currentArticle = allArticles.find(a => a.path === currentArticlePath);

    if (!currentArticle || !currentArticle.embedding) {
        // console.warn("Current article or embedding missing.");
        return [];
    }

    const currentEmbedding = currentArticle.embedding;
    const similarityScores = [];

    for (const article of allArticles) {
        // Skip comparing the article to itself or if it lacks an embedding
        if (article.path === currentArticlePath || !article.embedding) {
            continue;
        }

        const similarity = cosineSimilarity(currentEmbedding, article.embedding);
        similarityScores.push({ article, similarity });
    }

    // Sort by similarity (highest score first)
    similarityScores.sort((a, b) => b.similarity - a.similarity);

    // Return the top N articles
    return similarityScores.slice(0, topN).map(item => item.article);
};